import { Box } from '@mui/material';
import { Timestamp, where } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import {
  getDataFromCollection,
  getDataFromCollectionWithQuery,
  getPaginatedData,
} from '../../../MicrospecDynamicUI/Helpers/dataUtils';
import PagesLayout from '../../components/PagesLayout';
import CategoriesSection from './CategoriesSection';
import FeaturedProperties from './FeaturedProperties';
import HeroSection from './HeroSection';
import PropertiesGrid from './PropertiesGrid';

const PROPERTIES_PER_PAGE = 12;
const FEATURED_COUNT = 6;

const MainPage = () => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const [searchParams, setSearchParams] = useSearchParams();

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const selectedCategory = searchParams.get('category') || null;

  const setSelectedCategory = useCallback((categoryId: string | null) => {
    setSearchParams((prev) => {
      if (categoryId) {
        prev.set('category', categoryId);
      } else {
        prev.delete('category');
      }
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const [properties, setProperties] = useState<any[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [featuredProperties, setFeaturedProperties] = useState<any[]>([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);

  // Load categories
  useEffect(() => {
    if (!firestore?.instance) return;
    getDataFromCollection(
      (cats: any[]) => setCategories([...(cats || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) as any),
      (retrieved: boolean) => { if (retrieved) setCategoriesLoading(false); },
      firestore.instance,
      'categories',
      true,
      50
    );
  }, [firestore?.instance]);

  // If selected category doesn't exist in loaded categories, reset to all
  useEffect(() => {
    if (!categoriesLoading && selectedCategory && categories.length > 0) {
      const exists = categories.some((c: any) => c.id === selectedCategory);
      if (!exists) {
        setSelectedCategory(null);
      }
    }
  }, [categoriesLoading, selectedCategory, categories, setSelectedCategory]);

  // Load featured properties
  useEffect(() => {
    if (!firestore?.instance) return;
    setFeaturedLoading(true);

    const featuredWheres = [
      where('active', '==', true),
      where('approval_status', '==', 'approved'),
      where('featured', '==', true),
      where('feature_end_date', '>', Timestamp.now()),
    ];
    if (selectedCategory) {
      featuredWheres.push(where('category_id', '==', selectedCategory));
    }

    getDataFromCollectionWithQuery(
      (data: any[]) => setFeaturedProperties(data || []),
      (retrieved: boolean) => { if (retrieved) setFeaturedLoading(false); },
      firestore.instance,
      'properties',
      featuredWheres,
      false,
      FEATURED_COUNT
    );
  }, [firestore?.instance, selectedCategory]);

  // Load properties (initial + on category change)
  useEffect(() => {
    if (!firestore?.instance) return;
    setPropertiesLoading(true);
    setProperties([]);
    setLastDoc(null);
    setHasMore(true);

    const wheres = [where('active', '==', true), where('approval_status', '==', 'approved')];
    if (selectedCategory) {
      wheres.push(where('category_id', '==', selectedCategory));
    }

    getDataFromCollectionWithQuery(
      (data: any[]) => {
        setProperties(data);
        if (data.length > 0) {
          setLastDoc(data[data.length - 1]);
        }
        setHasMore(data.length >= PROPERTIES_PER_PAGE);
      },
      (retrieved: boolean) => { if (retrieved) setPropertiesLoading(false); },
      firestore.instance,
      'properties',
      wheres,
      false,
      PROPERTIES_PER_PAGE
    );
  }, [firestore?.instance, selectedCategory]);

  // Load more properties
  const loadMore = useCallback(() => {
    if (!firestore?.instance || !lastDoc || loadingMore) return;
    setLoadingMore(true);

    const wheres = [where('active', '==', true), where('approval_status', '==', 'approved')];
    if (selectedCategory) {
      wheres.push(where('category_id', '==', selectedCategory));
    }

    // @ts-ignore - getPaginatedData is untyped JS
    // eslint-disable-next-line
    getPaginatedData(
      lastDoc,
      PROPERTIES_PER_PAGE,
      'properties',
      firestore,
      'desc',
      // @ts-ignore
      'updated_at',
      wheres
    ).then((data: any[]) => {
      setProperties((prev) => [...prev, ...data]);
      if (data.length > 0) {
        setLastDoc(data[data.length - 1]);
      }
      setHasMore(data.length >= PROPERTIES_PER_PAGE);
      setLoadingMore(false);
    });
  }, [firestore, lastDoc, loadingMore, selectedCategory]);

  return (
    <PagesLayout>
      <HeroSection />
      <CategoriesSection
        categories={categories}
        loading={categoriesLoading}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />
      <FeaturedProperties
        properties={featuredProperties}
        loading={featuredLoading}
        categories={categories}
      />
      <Box id="properties-section">
        <PropertiesGrid
          properties={properties}
          loading={propertiesLoading}
          categories={categories}
          hasMore={hasMore}
          loadMore={loadMore}
          loadingMore={loadingMore}
        />
      </Box>
    </PagesLayout>
  );
};

export default MainPage;
